import { IconServer, IconCirclesFour, IconChart01, IconSettings, IconTestTube, IconGlobe, IconHome, IconDocs, IconMessageSquareV2 } from 'components/Icons';
import { CalloutBadge } from 'components/Badge/CalloutBadge';
import { useFeatureFlagsLD } from 'modules/FeatureFlagsLD/FeatureFlagsProviderLD';
import { useCurrentUserId, useIsLoggedIn, useIsOrgAccount } from 'modules/Auth/hooks';
import { useCurrentUserOrg } from 'modules/Organizations/hocs';
import { useOrgHasTeamRole } from 'modules/Organizations/hooks';
import { ORGANIZATION_ROLE_ID_ADMIN, ORGANIZATION_ROLE_ID_FINANCIAL_MANAGER, ORGANIZATION_ROLE_ID_MEMBER_MIN } from 'modules/Organizations/constants';
import { useIsViewableAsOrg } from 'modules/UsageDashboard/constants/accessLevel';
import { css } from '@linaria/core';
import { baseTheme } from 'styles/utils';
import { DOCUMENTATION_LINK } from 'constants/externalLinks';
import { useDataCollection } from 'modules/AnalyticsCollection/AnalyticsCollectionProvider';
import { JiraButton, JiraLinks } from 'components/Jira';
import { Menu } from './Menu';
import { WorkspaceSwitcher } from './WorkspaceSwitcher';
import { AppSelector } from './AppSelector';
export const NewBadge: React.FC = () => <CalloutBadge tight color="#FFFFFF" background="#12B76A" style={{
  fontWeight: 600
}}>
    New
  </CalloutBadge>;
export const OrgSettingsLinks: React.FC = () => {
  const org = useCurrentUserOrg();
  const orgHasTeamRole = useOrgHasTeamRole();
  const isAdminOrOrgMember = org?.role?.id !== ORGANIZATION_ROLE_ID_MEMBER_MIN;
  const isAdmin = org?.role?.id === ORGANIZATION_ROLE_ID_ADMIN;
  const isFinanicalManager = org?.role?.id === ORGANIZATION_ROLE_ID_FINANCIAL_MANAGER;
  const currentUserId = useCurrentUserId();
  const basePath = `/${currentUserId}`;
  return <Menu.Group title="Settings" highlightOnHref="/[userOrOrgId]/settings" icon={<IconSettings size={20} />}>
      <Menu.Item href={`${basePath}/settings`}>Account</Menu.Item>
      {isAdmin && <>
          <Menu.Item href={`${basePath}/settings/security`}>Security</Menu.Item>
        </>}
      {(isAdmin || isFinanicalManager) && <Menu.Item href={`${basePath}/settings/billing`}>Billing</Menu.Item>}
      {isAdmin && <Menu.Item href={`${basePath}/settings/members`}>Members</Menu.Item>}
      {isAdminOrOrgMember && orgHasTeamRole && <Menu.Item href={`${basePath}/settings/teams`}>Teams</Menu.Item>}
    </Menu.Group>;
};
export const UserSettingsLinks: React.FC = () => {
  const userId = useCurrentUserId();
  return <Menu.Group title="Settings" highlightOnHref="/[userOrOrgId]/settings" icon={<IconSettings size={20} />}>
      <Menu.Item href={`/${userId}/settings`}>Account</Menu.Item>
      <Menu.Item href={`/${userId}/settings/billing`}>Billing</Menu.Item>
      <Menu.Item href={`/${userId}/settings/secrets`}>Secrets</Menu.Item>
      <Menu.Item href={`/${userId}/settings/security`}>Account Security</Menu.Item>
      <Menu.Item href={`/${userId}/settings/organizations`}>Manage Organizations</Menu.Item>
    </Menu.Group>;
};
export const SettingsLinks: React.FC = () => {
  const isLoggedIn = useIsLoggedIn();
  const isOrg = useIsOrgAccount();
  if (!isLoggedIn) {
    return null;
  }
  return isOrg ? <OrgSettingsLinks key="org-settings-links" /> : <UserSettingsLinks key="user-settings-links" />;
};
export const ComputeLinks: React.FC = () => {
  const userId = useCurrentUserId();
  const {
    enableLocalRunnersLDFF
  } = useFeatureFlagsLD();
  if (!userId) {
    return <Menu.Item href={`/${userId}/compute`} icon={<IconServer size={20} strokeWidth={1.5} />} standalone>
        Compute
      </Menu.Item>;
  }
  return <Menu.Group title="Compute" highlightOnHref="/[userOrOrgId]/compute" icon={<IconServer size={20} strokeWidth={1.5} />}>
      <Menu.Item href={`/${userId}/compute`}>Compute Overview</Menu.Item>
      <Menu.Item href={`/${userId}/compute/clusters`}>Clusters</Menu.Item>
      <Menu.Item href={`/${userId}/compute/nodepools`}>Nodepools</Menu.Item>
      <Menu.Item href={`/${userId}/compute/deployments`}>Deployments</Menu.Item>
      {enableLocalRunnersLDFF && <Menu.Item href={`/${userId}/compute/runners`} rightSection={<NewBadge />}>
          Runners
        </Menu.Item>}
    </Menu.Group>;
};
export const ControlCenterLinks: React.FC = () => {
  const userId = useCurrentUserId();
  const isViewableAsOrg = useIsViewableAsOrg();
  const {
    controlCenterExternal
  } = useFeatureFlagsLD();
  const isVisible = (href: string) => {
    const pathWithoutQueryParams = href.split('?')[0];
    const pathWithoutUserId = pathWithoutQueryParams.replace(/^\/[^/]+/, '');
    return isViewableAsOrg(pathWithoutUserId);
  };
  if (!userId) {
    return null;
  }
  return <Menu.Group title="Control Center" highlightOnHref="/[userOrOrgId]/control-center" icon={<IconChart01 size={20} strokeWidth={1.5} />}>
      <Menu.Item href={`/${userId}/control-center/overview`} visible={isVisible(`/${userId}/control-center/overview`)}>
        Overview
      </Menu.Item>
      <Menu.Item href={`/${userId}/control-center/usage`} visible={isVisible(`/${userId}/control-center/usage`)}>
        Usage & Operations
      </Menu.Item>
      <Menu.Item href={`/${userId}/control-center/costs`} visible={isVisible(`/${userId}/control-center/costs`)}>
        Costs & Budget
      </Menu.Item>
      <Menu.Item href={`/${userId}/control-center/external`} visible={!!controlCenterExternal}>
        External Usage
      </Menu.Item>
      <Menu.Item href={`/${userId}/control-center/teams-logs`} visible={isVisible(`/${userId}/control-center/teams-logs`)}>
        Teams & Logs
      </Menu.Item>
    </Menu.Group>;
};
export const ProjectsLinks: React.FC = () => {
  const userId = useCurrentUserId();
  const addAllResourcesSuffix = (prefixRoute: string) => ['apps', 'models', 'workflows', 'datasets', 'modules', 'leaderboard', 'collaborations'].map(suffix => `/${prefixRoute}/${suffix}`);
  if (!userId) {
    return null;
  }
  const highlightOnHref = [`/[userOrOrgId]/[appId]`, ...addAllResourcesSuffix('[userOrOrgId]')];
  return <Menu.Group title="Projects" highlightOnHref={highlightOnHref} icon={<IconCirclesFour size={20} strokeWidth={1.25} />}>
      <AppSelector />
      <Menu.Item href={`/${userId}/apps`} highlightOnHref={addAllResourcesSuffix(userId)}>
        All Resources
      </Menu.Item>
    </Menu.Group>;
};
export const FixedBottomLinks: React.FC = () => {
  const {
    track
  } = useDataCollection();
  const onHelpMenuClick = (btnText: string): void => {
    track('Help Menu Clicked', {
      param1: 'main_header',
      param2: btnText
    });
  };
  return <div className={SidebarContentStyles.fixedBottomMenuStack}>
      <Menu.Item href={DOCUMENTATION_LINK} target="_blank" icon={<IconDocs size={20} strokeWidth={1.5} />} onClick={() => onHelpMenuClick('Documentation')} standalone>
        Documentation
      </Menu.Item>
      <JiraButton collectorId={JiraLinks.sendFeedback.collectorId} clickTargetSelector={JiraLinks.sendFeedback.clickTargetSelector}>
        <Menu.Item icon={<IconMessageSquareV2 size={20} strokeWidth={1.5} />} onClick={() => onHelpMenuClick('Send feedback')} standalone>
          Send feedback
        </Menu.Item>
      </JiraButton>
    </div>;
};

// Custom Community menu item that uses our custom highlighting logic
const CommunityMenuItem: React.FC = () => {
  return <Menu.Item matchInitialHrefForHighlighting href="/explore" icon={<IconGlobe size={20} strokeWidth={1.5} />} standalone>
      Community
    </Menu.Item>;
};
export const SidebarContent: React.FC = () => {
  const currentUserId = useCurrentUserId();
  const basePath = currentUserId ? `/${currentUserId}` : '';
  return <>
      <Menu.Stack className={SidebarContentStyles.wrapperMenuStack}>
        <WorkspaceSwitcher />
        <Menu.Item href={`${basePath}/home`} icon={<IconHome size={20} strokeWidth={1.25} />} standalone>
          Home
        </Menu.Item>
        <ProjectsLinks />
        <ComputeLinks />
        <ControlCenterLinks />
        <Menu.Item href={`${basePath}/playground`} icon={<IconTestTube size={20} />} standalone>
          Playground
        </Menu.Item>
        <CommunityMenuItem />
        <SettingsLinks />
        <FixedBottomLinks />
      </Menu.Stack>
    </>;
};
export const SidebarContentStyles = {
  wrapperMenuStack: "omponents__SidebarV2__SidebarContent_tsx-wrapperMenuStack-w1nm4fnd",
  fixedBottomMenuStack: "omponents__SidebarV2__SidebarContent_tsx-fixedBottomMenuStack-f1legwmn"
};

require("./SidebarContent.linaria.module.css!=!../../node_modules/@linaria/webpack-loader/node_modules/@linaria/webpack5-loader/lib/outputCssLoader.js!./SidebarContent.tsx");